import React, { Ref } from 'react'
import { ErrorCode } from 'react-dropzone'

import { Text } from '@factorialco/deprecated-design-system'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { TextNode } from 'lexical'

import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Editor, { type EditorHandle, EditorProps } from './Editor'
import * as styles from './index.vanilla.css'
import { ExtendedTextNode } from './nodes/ExtendedTextNode'
import { MentionNode } from './nodes/MentionNode'
import TextEditorNodes from './nodes/TextEditorNodes'
import { MentionedAccess } from './plugins/DebouncedMentionsPlugin'
import { MentionedUser } from './plugins/MentionsPlugin'
import TextEditorTheme from './themes/TextEditorTheme'

type Props = Omit<
  EditorProps,
  | 'selectValue'
  | 'missingLabel'
  | 'onDropRejected'
  | 'maxFileSizeError'
  | 'saveButtonLabel'
  | 'removeButtonLabel'
  | 'errorLabelFn'
> & {
  hasMentions?: boolean
  hasDebouncedMentions?: boolean
  onMentionQueryStringChanged?: (queryString: string) => Promise<MentionedAccess[]> | undefined
  users?: MentionedUser[]
  missingLabel?: string
}

const TextEditor = React.forwardRef(function TextEditor(props: Props, ref: Ref<EditorHandle>) {
  const {
    onChange,
    onFiles,
    initialEditorState,
    maxFileSize,
    height,
    placeholder,
    readonly,
    isRichText = true,
    enableBetaFeatures = true,
    missingLabel,
  } = props

  const initialConfig = {
    namespace: 'TextEditor',
    nodes: [
      ...TextEditorNodes,
      MentionNode,
      ExtendedTextNode,
      {
        replace: TextNode,
        with: (node: TextNode) => new ExtendedTextNode(node.__text),
        withKlass: ExtendedTextNode,
      },
    ],
    onError: (error: Error) => {
      throw error
    },
    theme: TextEditorTheme,
    editable: readonly,
  }

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <>
        <div className={styles.editorShell + ' ' + (!isRichText ? styles.plainEditorWrapper : '')}>
          <Editor
            {...props}
            ref={ref}
            onChange={onChange}
            onFiles={onFiles}
            maxFileSize={maxFileSize}
            height={height}
            initialEditorState={initialEditorState}
            placeholder={placeholder}
            readonly={readonly}
            isRichText={isRichText}
            enableBetaFeatures={enableBetaFeatures}
            missingLabel={missingLabel ?? i18n.t('common.missing_field')}
            selectValue={i18n.t('common.select')}
            onDropRejected={(rejections) => {
              // TODO: Improve to explain other reasons for failure.
              if (
                rejections.some((rejection) =>
                  rejection.errors.some((reason) => reason.code === ErrorCode.FileTooLarge)
                )
              ) {
                app.setFlash({
                  type: 'wrong',
                  message: i18n.t('common.file_size_limit', {
                    size: maxFileSize,
                  }),
                })
              }
            }}
            saveButtonLabel={i18n.t('common.text_editor.floating_link_plugin.save_button_label')}
            removeButtonLabel={i18n.t(
              'common.text_editor.floating_link_plugin.remove_button_label'
            )}
            errorLabelFn={(validUrl, linkUrl) => {
              return validUrl || linkUrl === ''
                ? undefined
                : i18n.t('common.text_editor.floating_link_plugin.error')
            }}
          />
        </div>
        {maxFileSize && (
          <Text.Normal>
            {i18n.t('common.file_size_limit', {
              size: maxFileSize,
            })}
          </Text.Normal>
        )}
      </>
    </LexicalComposer>
  )
})

export type { EditorHandle }

export default TextEditor
